// Konfiguraation olennaisen version tunniste (V2-T3, PL-14).
//
// Suostumus on annettu tietylle tarkoitusjoukolle. Jos julkaistun
// konfiguraation OLENNAINEN sisältö muuttuu, aiempi suostumus ei enää kata
// uutta tilannetta: banneri on näytettävä uudelleen eikä vanha valinta saa
// vapauttaa skriptejä.
//
// Olennaiseksi luetaan (= muutos johtaa uudelleenkysyntään):
//   - kategorioiden joukko (uusi tai poistettu kategoria)
//   - kategorian välttämättömyys (is_required)
//   - kategorian oletustila (is_default_enabled)
//   - kategorian nimi ja kuvaus eli tarkoitus, kaikilla kielillä
//   - kategorian Google Consent Mode -mappaus (mitä signaaleja valinta ohjaa)
//
// Olennaiseksi EI lueta (= ei uudelleenkysyntää):
//   - bannerin värit, sijainti, ulkoasu, painikkeiden tekstit ja
//     kerrosten otsikot/kuvaukset: ulkoasu ja sanamuoto voivat muuttua ilman
//     että kävijän hyväksymä tarkoitus muuttuu
//   - konfiguraation version-numero sellaisenaan (juokseva numero muuttuu
//     joka julkaisussa; pelkkä numero ei kerro tarkoituksen muuttuneesta)
//   - suostumuksen voimassaoloaika (koskee vasta uusia valintoja)
//   - kategorian evästelista: skanneri päivittää listaa automaattisesti, ja
//     jokaisesta listamuutoksesta kysyminen johtaisi suostumusväsymykseen.
//     Lista näkyy kävijälle bannerin toisella tasolla, ja kun tarkoitus
//     muuttuu, muuttuu myös kategorian kuvaus — se on olennainen kenttä.
//     (Kirjattu päätöslokiin PL-14 tietoisena rajanvetona; juridinen
//     katselmointi vahvistaa rajauksen.)

import type { CmpConfig } from './types';

/**
 * Kanoninen esitys konfiguraation olennaisesta sisällöstä. Järjestys on
 * vakioitu (slugit ja kielet lajitellaan), jotta sama sisältö tuottaa aina
 * saman tuloksen riippumatta tietokannan palautusjärjestyksestä.
 */
export function materialConfigContent(config: CmpConfig): string {
  const cats = (config.categories || [])
    .slice()
    .sort((a, b) => (a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0))
    .map(function (cat) {
      const texts = cat.texts || {};
      const langs = Object.keys(texts).sort();
      return {
        slug: cat.slug,
        req: !!cat.is_required,
        def: !!cat.is_default_enabled,
        gcm: (cat.gcm_consent_types || []).slice().sort(),
        texts: langs.map(function (l) {
          const t = texts[l] || {};
          return [l, t.name || '', t.description || ''];
        }),
      };
    });
  // Skeemamerkki: jos olennaisen sisällön määritelmä muuttuu, merkki nostetaan
  // ja kaikki suostumukset kysytään kertaalleen uudelleen.
  return JSON.stringify({ schema: 'v2', cats });
}

/**
 * Vakaa, lyhyt tiiviste merkkijonosta (kaksi eri 32-bittistä algoritmia
 * peräkkäin). Käyttötarkoitus on muutoksen havaitseminen, ei suojaus —
 * suostumuksen todistusketjun hash on erikseen (SHA-256, V2-T9).
 */
export function stableHash(str: string): string {
  let fnv = 0x811c9dc5;
  let djb = 5381;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    fnv ^= c;
    fnv = Math.imul(fnv, 0x01000193);
    djb = (Math.imul(djb, 33) + c) | 0;
  }
  const hex = (n: number) => (n >>> 0).toString(16).padStart(8, '0');
  return hex(fnv) + hex(djb);
}

/** Konfiguraation olennaisen sisällön tunniste (tallennetaan suostumukseen). */
export function configFingerprint(config: CmpConfig): string {
  return stableHash(materialConfigContent(config));
}

/**
 * Kattaako tallennettu suostumus nykyisen konfiguraation. Tuntematon
 * tunniste (vaihetta 2 edeltävä tallenne) hyväksytään: sitä ei ole
 * annettu mitään vastaan, jota voisi verrata, eikä kävijää ole reilua
 * kysyttää uudelleen tiedon puutteen vuoksi. Seuraava valinta kirjaa
 * tunnisteen, jonka jälkeen vertailu toimii.
 */
export function consentCoversConfig(storedFingerprint: string | undefined | null, config: CmpConfig): boolean {
  if (!storedFingerprint) return true;
  return storedFingerprint === configFingerprint(config);
}
